import { useTranslation } from '@hooks';
import { Recommendation } from '@constants/dashboard';
import { Completeness } from '@constants/companies';
import { SYNC_VIEW } from '@constants/profiles';
import { RoutePath } from '@constants/routes';

const INCOMPLETE_LEVELS = encodeURIComponent(
    JSON.stringify([Completeness.Critical, Completeness.Low, Completeness.Average]),
);
import { SurveysIcon, ReviewsIcon, SocialIcon, MessagingIcon } from 'assets/images/sidebar';
import { ListingsIcon, ErrorIcon, LocationsIcon } from 'assets/images/icons';

export const useRecommendationsMap = () => {
    const { t } = useTranslation();

    return {
        [Recommendation.AutomationFailed]: {
            icon: <ErrorIcon />,
            url: RoutePath.CampaignsAutomated,
            urlText: t('HomePage.recommendation10UrlText'),
            title: t('HomePage.recommendation10Title'),
            text: t('HomePage.recommendation10Text'),
            isError: true,
        },
        [Recommendation.OneTimeCampaignFailed]: {
            icon: <ErrorIcon />,
            url: RoutePath.CampaignsOneTime,
            urlText: t('HomePage.recommendation10UrlText'),
            title: t('HomePage.recommendation11Title'),
            text: t('HomePage.recommendation11Text'),
            isError: true,
        },
        [Recommendation.IntegrationDisconnected]: {
            icon: <ErrorIcon />,
            url: RoutePath.AccountSettingsIntegrations,
            urlText: t('HomePage.recommendation3UrlText'),
            title: t('HomePage.recommendation8Title'),
            text: t('HomePage.recommendation8Text'),
            isError: true,
        },
        [Recommendation.ChannelDisconnected]: {
            icon: <ErrorIcon />,
            url: RoutePath.AccountSettingsChannels,
            urlText: t('HomePage.recommendation3UrlText'),
            title: t('HomePage.recommendation9Title'),
            text: t('HomePage.recommendation9Text'),
            isError: true,
        },
        [Recommendation.UnrepliedReviews]: {
            icon: <ReviewsIcon />,
            url: `${RoutePath.ReviewsManage}?withoutReplies=true`,
            urlText: t('HomePage.recommendation1UrlText'),
            title: t('HomePage.recommendation1Title'),
            subtitle: t('HomePage.recommendation1Subtitle'),
            text: t('HomePage.recommendation1Text'),
            chip: value => `${value} ${t('common.review', { count: value })}`,
        },
        [Recommendation.OutdatedPhotos]: {
            icon: <LocationsIcon />,
            url: RoutePath.LocationsMedia,
            urlText: t('HomePage.recommendation2UrlText'),
            title: t('HomePage.recommendation2Title'),
            subtitle: t('HomePage.recommendation2Subtitle'),
            text: t('HomePage.recommendation2Text'),
            chip: value => `${value} ${t('common.location', { count: value })}`,
        },
        [Recommendation.IncompleteLocations]: {
            icon: <ListingsIcon />,
            url: `${RoutePath.LocationsManage}?completenessLevels=${INCOMPLETE_LEVELS}`,
            urlText: t('HomePage.recommendationLocationsUrlText'),
            title: t('HomePage.recommendation3Title'),
            subtitle: t('HomePage.recommendation3Subtitle'),
            text: t('HomePage.recommendation3Text'),
            chip: value => `${value} ${t('common.location', { count: value })}`,
        },
        [Recommendation.ActionRequiredProfiles]: {
            icon: <LocationsIcon />,
            url: `${RoutePath.LocationsProfiles}?view=[${SYNC_VIEW.CONFLICT},${SYNC_VIEW.NOT_PUBLISHED}]`,
            urlText: t('HomePage.recommendation4UrlText'),
            title: t('HomePage.recommendation4Title'),
            subtitle: t('HomePage.recommendation4Subtitle'),
            text: t('HomePage.recommendation4Text'),
            chip: value => `${value} ${t('common.profile', { count: value })}`,
        },
        [Recommendation.UnreadConversations]: {
            icon: <MessagingIcon />,
            url: RoutePath.MessagingInbox,
            urlText: t('HomePage.recommendation5UrlText'),
            title: t('HomePage.recommendation5Title'),
            subtitle: t('HomePage.recommendation5Subtitle'),
            text: t('HomePage.recommendation5Text'),
            chip: value => `${value} ${t('common.message', { count: value })}`,
        },
        [Recommendation.OutdatedSocialPosts]: {
            icon: <SocialIcon />,
            url: RoutePath.SocialPosts,
            urlText: t('HomePage.recommendation6UrlText'),
            title: t('HomePage.recommendation6Title'),
            subtitle: t('HomePage.recommendation6Subtitle'),
            text: t('HomePage.recommendation6Text'),
            chip: value => `${value} ${t('common.location', { count: value })}`,
        },
        [Recommendation.UnreadSurveyResponses]: {
            icon: <SurveysIcon />,
            url: `${RoutePath.SurveysResponses}?rating=[1,2,3]&hideAnonimous=true`,
            urlText: t('HomePage.recommendation7UrlText'),
            title: t('HomePage.recommendation7Title'),
            subtitle: t('HomePage.recommendation7Subtitle'),
            text: t('HomePage.recommendation7Text'),
            chip: value => `${value} ${t('common.complaint', { count: value })}`,
        },
    };
};
